import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './CompetitorAlert.css';
import FeedbackPopover from './FeedbackPopover';
import { updateCompetitorFeedback } from '../store/slices/competitorSlice';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';

const CompetitorAlert = ({ alert, onDismiss, autoExpand = false }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [isExpanded, setIsExpanded] = useState(autoExpand || alert.priority === 'high');
  const [copySuccess, setCopySuccess] = useState({});

  const handleFeedbackSubmit = async (rating, reason) => {
    const roomId = sessionManager.getRoomId();

    // Update Redux immediately so UI reflects the choice even if persist fails
    dispatch(updateCompetitorFeedback({
      detected_at: alert.detected_at,
      feedback: { rating, reason: reason || undefined, submittedAt: new Date().toISOString() }
    }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'competitor',
        entryTimestamp: alert.detected_at,
        rating,
        reason
      });
    } catch (err) {
      console.warn('[CompetitorAlert] Entry feedback persist failed:', err);
    }
  };

  useEffect(() => {
    // Auto-expand high priority alerts
    if (alert.priority === 'high' && !isExpanded) {
      setIsExpanded(true);
    }
  }, [alert.priority]);

  const handleCopyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(prev => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setCopySuccess(prev => ({ ...prev, [id]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const getPriorityClass = () => {
    switch (alert.priority) {
      case 'high':
        return 'competitor-alert-high';
      case 'medium':
        return 'competitor-alert-medium';
      case 'low':
        return 'competitor-alert-low';
      default:
        return 'competitor-alert-medium';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'pricing':
        return '💰';
      case 'features':
        return '⚡';
      case 'implementation':
        return '🔧';
      case 'support':
        return '🤝';
      case 'performance':
        return '📊';
      case 'reliability':
        return '🛡️';
      default:
        return '📌';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return '#4CAF50';
    if (confidence >= 0.75) return '#FFA726';
    return '#FF7043';
  };

  return (
    <div className={`competitor-alert ${getPriorityClass()} ${isExpanded ? 'expanded' : ''}`}>
      <div className="competitor-alert-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="competitor-alert-title">
          <span className="competitor-icon">🎯</span>
          <strong>{alert.competitor}</strong> mentioned
          {alert.speaker && (
            <span className="speaker-badge">by {alert.speaker}</span>
          )}
        </div>
        <div className="competitor-alert-controls">
          <span
            className="confidence-badge"
            style={{ backgroundColor: getConfidenceColor(alert.confidence) }}
          >
            {Math.round(alert.confidence * 100)}% match
          </span>
          <div onClick={e => e.stopPropagation()}>
            <FeedbackPopover
              currentFeedback={alert.feedback}
              onSubmit={handleFeedbackSubmit}
            />
          </div>
          <button
            className="expand-toggle"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
          <button
            className="dismiss-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss(alert.id);
            }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="competitor-alert-content">
          {alert.matched_text && (
            <div className="matched-text">
              <small>Detected: "{alert.matched_text}"</small>
            </div>
          )}

          {alert.talking_points && (
            <div className="talking-points">
              <h4>💡 Quick Response Guide:</h4>
              <p>{alert.talking_points}</p>
            </div>
          )}

          {alert.advantages && alert.advantages.length > 0 && (
            <div className="advantages-section">
              <h4>✅ Our Advantages ({alert.advantages.length}):</h4>
              <div className="advantages-list">
                {[...alert.advantages]
                  .sort((a, b) => (b.priority || 0) - (a.priority || 0))
                  .map((advantage, index) => {
                    // Handle both object and string formats for backwards compatibility
                    const advantagePoint = typeof advantage === 'string' ? advantage : advantage.point;
                    const advantageCategory = typeof advantage === 'object' ? advantage.category : 'other';
                    const advantageEvidence = typeof advantage === 'object' ? advantage.evidence : null;
                    const advantagePriority = typeof advantage === 'object' ? advantage.priority : 0;

                    return (
                      <div key={index} className="advantage-item">
                        <div className="advantage-header">
                          <span className="category-icon">
                            {getCategoryIcon(advantageCategory)}
                          </span>
                          <span className="category-label">
                            {advantageCategory?.toUpperCase()}
                          </span>
                          {advantagePriority >= 4 && (
                            <span className="priority-star">⭐</span>
                          )}
                        </div>
                        <div className="advantage-content">
                          <p className="advantage-point">{advantagePoint}</p>
                          {advantageEvidence && (
                            <p className="advantage-evidence">
                              <em>Evidence: {advantageEvidence}</em>
                            </p>
                          )}
                        </div>
                        <button
                          className={`copy-btn ${copySuccess[`adv-${index}`] ? 'success' : ''}`}
                          onClick={() => handleCopyToClipboard(
                            `${advantagePoint}${advantageEvidence ? ` (${advantageEvidence})` : ''}`,
                            `adv-${index}`
                          )}
                        >
                          {copySuccess[`adv-${index}`] ? '✓ Copied' : '📋 Copy'}
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {alert.weaknesses && alert.weaknesses.length > 0 && (
            <div className="weaknesses-section">
              <h4>🎯 Their Weaknesses ({alert.weaknesses.length}):</h4>
              <div className="weaknesses-list">
                {alert.weaknesses.map((weakness, index) => {
                  // Handle both object and string formats for backwards compatibility
                  const weaknessPoint = typeof weakness === 'string' ? weakness : weakness.point;
                  const weaknessCategory = typeof weakness === 'object' ? weakness.category : 'other';

                  return (
                    <div key={index} className="weakness-item">
                      <span className="category-icon">
                        {getCategoryIcon(weaknessCategory)}
                      </span>
                      <span className="weakness-point">{weaknessPoint}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="alert-footer">
            <span className="match-type">Match type: {alert.match_type}</span>
            <span className="timestamp">
              {new Date(alert.detected_at).toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const CompetitorAlertsContainer = ({ alerts, onDismiss, position = 'right' }) => {
  return (
    <div className={`competitor-alerts-container ${position}`}>
      {alerts.length > 0 && (
        <div className="competitor-alerts-header">
          <h3>🎯 Competitor Analysis</h3>
          <span className="alert-count">{alerts.length} active</span>
        </div>
      )}
      <div className="competitor-alerts-list">
        {alerts.map((alert) => (
          <CompetitorAlert
            key={alert.id}
            alert={alert}
            onDismiss={onDismiss}
            autoExpand={alert.priority === 'high'}
          />
        ))}
      </div>
    </div>
  );
};

export default CompetitorAlert;